import React, { forwardRef, useCallback, useMemo, useState } from 'react';
import {
    ButtonGroup,
    Menu,
    MenuItem,
    useMediaQuery,
    useTheme
} from '@mui/material';
import CbgContext from '../CbgContext';
import useButtonAnchor from '@dyl/hooks/useButtonAnchor';
import { omit } from 'lodash';

/** @type {import('../CbgContext').CbgEntry[]} */
const createInitialState = () => [];

/**
 * @param {import('@mui/material').ButtonGroupProps} props
 */
export default function CollapsibleAssetButtonGroup({ children, ...other }) {
    const [registry, setRegistry] = useState(createInitialState);

    const update = useCallback(entry => {
        setRegistry(prev => {
            const isNew = !prev.some(e => e.id === entry.id);

            if (isNew) {
                return [...prev, entry];
            }

            return prev.map(e => {
                if (e.id === entry.id) {
                    return entry;
                }

                return e;
            });
        });
    }, []);

    const unregister = useCallback(id => {
        setRegistry(prev => prev.filter(e => e.id !== id));
    }, []);

    const contextValue = useMemo(() => ({
        update,
        unregister,
    }), [update, unregister]);

    const { breakpoints } = useTheme();

    const upFromDown = useCallback(down => {
        const index = breakpoints.keys.indexOf(down);

        return breakpoints.keys[index + 1];
    }, [breakpoints]);

    const [menuAnchor, closeMore] = useButtonAnchor();

    return (
        <>
            <CbgContext.Provider value={contextValue}>
                <ButtonGroup {...other}>
                    {/* The children actually only set up the logic for rendering the buttons */}
                    {children}
                </ButtonGroup>
            </CbgContext.Provider>

            <Menu
                open={!!menuAnchor}
                anchorEl={menuAnchor}
                onClose={closeMore}
            >
                {registry.map(({ id, down, props }) => (
                    <HiddenUpDyl key={id} up={upFromDown(down)}>
                        <MenuItem {...simplifyMenuItemProps(props)} />
                    </HiddenUpDyl>
                ))}
            </Menu>
        </>
    );
}

const HiddenUpDyl = forwardRef(function HiddenUpDyl({ up, children }, ref) {
    const { breakpoints } = useTheme();
    const hide = useMediaQuery(breakpoints.up(up));

    return !hide && React.cloneElement(children, { ref });
});

function simplifyMenuItemProps(buttonProps) {
    return omit(buttonProps, [
        'ref',
        'disableElevation',
        'disableFocusRipple',
        'fullWidth',
    ])
}